<template>
  <label for="teeth" class="input-teeth">
    <span
      class="input-teeth__label"
      :class="{ 'visually-hidden': titleHidden }"
    >
      {{ title }}
    </span>
    <masked-input
      id="teeth-input"
      aria-label="Введите номер зуба"
      class="input-teeth__input"
      ref="maskedInput"
      inputmode="numeric"
      v-bind:required="required"
      v-bind:aria-invalid="hasViolations"
      v-bind:guide="true"
      v-bind:mask="$options.teethMaskWithDelimitersChars"
      v-bind:pattern="$options.teethPatternRegEx"
      v-bind:placeholder="$options.teethPlaceholder"
      v-bind:value="maskedInputValue"
      v-on:input="onInput" />
    <p
      class="input-teeth__violation"
      role="alert"
      v-for="violationText in violations"
      v-bind:key="violationText"
    >{{ violationText }}</p>
  </label>
</template>
<script>
import MaskedInput from 'vue-text-mask';

/**
 * @description Шаблон маски ввода зуба.
 * @type {string}
 */
const teethMaskWithDelimiters = '##';

/**
 * @description Символы и выражения маски зуба с разделителями.
 * @type {(RegExp|string)[]}
 */
const teethMaskWithDelimitersChars = Array
  .from(teethMaskWithDelimiters)
  .map((char) => (char === '#' ? /[0-9]/ : char));

/**
 * @description Заполнитель ввода зуба.
 * @type {string}
 */
const teethPlaceholder = '№ зуба';

/**
 * @description Регулярное выражение для ввода зубов.
 * @type {string}
 */
const teethPatternRegEx = '^\\d{2}$';

/**
 * @description Компонент ввода однострочного текста.
 * @type {Vue}
 */
export default {
  components: { MaskedInput },
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0;
    },
  },
  data() {
    return {
      /**
       * @description Замаскированное значение.
       * @type {string}
       */
      maskedInputValue: '',
    };
  },
  methods: {
    /**
     * @description Выполнить ввод
     * @param value
     */
    onInput(value) {
      this.maskedInputValue = value;
      if (this.isValid) {
        this.$emit('input', value);
      } else {
        this.$emit('input', '');
      }
    },
    /**
     * @description Ввод корректен.
     * @type {boolean}
     */
    isValid() {
      return this.$options.teethPatternRegEx.test(this.maskedInputValue);
    },
  },
  mounted() {
    this.onInput(this.$refs.maskedInput.$el.value); // fix mask without input
  },
  name: 'InputTeeth',
  teethPlaceholder,
  teethMaskWithDelimitersChars,
  teethPatternRegEx,
  props: {
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { default: 'Поиск по зубу', type: String },
    /**
     * @description Значение.
     * @type {string}
     */
    value: {
      default: () => '',
      type: String,
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      type: Array,
    },
    /**
     * @description Обязательное поле
     * @type {Boolean}
     */
    required: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Доступное скрытие заголовка (подписи)
     * @type {Boolean}
     */
    titleHidden: {
      default: () => false,
      type: Boolean,
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.input-teeth {
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: 130px;
  &__label {
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 2px;
    color: var(--unfocus-color);
    &.visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      white-space: nowrap;
      -webkit-clip-path: inset(100%);
      clip-path: inset(100%);
      clip: rect(0 0 0 0);
      overflow: hidden;
    }
  }
  &__input {
    @include fields.input-field();
  }
  &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
  }
}
</style>
